import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Clock, Flame, Sunrise, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { suggestActivationForHour, computeStreak, type CompletionRow } from "@/lib/activations";
import { getTodaysActivation, markActivationSeen, type TodayActivation as TodayRow } from "@/lib/activations-today.functions";

/**
 * Unified daily card. Reads the single "today's activation" row via
 * getTodaysActivation() — the same source used across the ecosystem.
 * Falls back to the local time-of-day suggestion if none is published yet.
 */
export function TodayActivation() {
  const [today, setToday] = useState<TodayRow | null | undefined>(undefined);
  const [streak, setStreak] = useState<number | null>(null);
  const [authed, setAuthed] = useState<boolean>(false);
  const fetchToday = useServerFn(getTodaysActivation);
  const markSeen = useServerFn(markActivationSeen);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = (await fetchToday()) as TodayRow | null;
        if (!cancelled) setToday(row ?? null);
      } catch {
        if (!cancelled) setToday(null);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchToday]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!userData.user) { setAuthed(false); return; }
      setAuthed(true);
      const since = new Date();
      since.setDate(since.getDate() - 60);
      const { data } = await supabase
        .from("activation_completions")
        .select("activation_slug, completed_at")
        .gte("completed_at", since.toISOString())
        .order("completed_at", { ascending: false });
      if (cancelled) return;
      setStreak(computeStreak((data ?? []) as CompletionRow[]));
    })();
    return () => { cancelled = true; };
  }, []);

  // Mark as seen once we know the user is authed and the row loaded.
  useEffect(() => {
    if (!authed || !today?.id) return;
    void markSeen({ data: { activation_id: today.id } }).catch(() => {});
  }, [authed, today?.id, markSeen]);

  // Reserve the card's footprint while the row loads so the sections below it
  // do not shift (measured CLS 0.217 on /activations before this change).
  if (today === undefined) {
    return (
      <div
        aria-hidden
        className="mb-12 min-h-[260px] rounded-2xl border hairline bg-charcoal/40 sm:min-h-[220px]"
      />
    );
  }

  // No published activation → gentle local fallback.
  if (!today) {
    const a = suggestActivationForHour(new Date().getHours());
    return (
      <section
        aria-label="Today's suggested activation"
        className="mb-12 grid gap-6 rounded-2xl border-gold border bg-charcoal p-6 sm:p-8 lg:grid-cols-[1.4fr_1fr]"
      >
        <div>
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
            <Sunrise size={12} /> The hour asks for
          </p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-gold-luminous">{a.title}</h2>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">{a.intention}</p>
          <div className="mt-6">
            <Link
              to="/activations/$slug"
              params={{ slug: a.slug }}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
            >
              Enter this activation
            </Link>
          </div>
        </div>
        <StreakCard authed={authed} streak={streak} />
      </section>
    );
  }

  const bg = today.wallpaper_url || today.hero_image_url;

  return (
    <section
      aria-label="Today's KAfrequency activation"
      className="relative mb-12 overflow-hidden rounded-2xl border-gold border bg-charcoal"
    >
      {bg && (
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${bg})` }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-tr from-obsidian via-obsidian/80 to-transparent" aria-hidden />
      <div className="relative grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
            <Sparkles size={12} /> Today's Current
            {today.activation_number ? <span className="text-muted-foreground">· № {today.activation_number}</span> : null}
          </p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl text-gold-luminous">
            {today.title}
          </h2>
          {today.subtitle && <p className="mt-2 font-serif text-lg text-ivory/90">{today.subtitle}</p>}
          {today.intention && <p className="mt-3 max-w-xl text-sm text-muted-foreground">{today.intention}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {today.duration && (
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {today.duration}</span>
            )}
            {today.theme && <><span className="text-gold">·</span><span>{today.theme}</span></>}
            {today.difficulty && <><span className="text-gold">·</span><span>{today.difficulty}</span></>}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/activations/$slug"
              params={{ slug: today.slug }}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
            >
              Enter today's activation
            </Link>
            <Link
              to="/activations/archive"
              className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold"
            >
              Archive
            </Link>
          </div>
        </div>
        <StreakCard authed={authed} streak={streak} />
      </div>
    </section>
  );
}

function StreakCard({ authed, streak }: { authed: boolean; streak: number | null }) {
  return (
    <div className="relative flex flex-col justify-center gap-3 rounded-xl border hairline bg-obsidian/70 p-6">
      <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Discipline</p>
      {authed ? (
        <>
          <p className="inline-flex items-baseline gap-2 font-serif text-5xl text-gold-luminous">
            <Flame size={22} className="text-gold" aria-hidden />
            {streak ?? "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {streak && streak > 0
              ? `Consecutive day${streak === 1 ? "" : "s"} of practice.`
              : "Begin today. One completion opens the chain."}
          </p>
        </>
      ) : (
        <>
          <p className="font-serif text-2xl text-ivory">Track the chain.</p>
          <p className="text-xs text-muted-foreground">Sign in to keep a streak of consecutive days of practice.</p>
          <Link to="/auth" className="mt-2 text-xs uppercase tracking-[0.25em] text-gold hover:underline">
            Sign in →
          </Link>
        </>
      )}
    </div>
  );
}
